import { supabase } from '../lib/supabaseClient';

/**
 * Fetch comments for a specific recommendation
 * @param {number} recommendationId - The recommendation ID to fetch comments for
 * @returns {Array} - Array of comments with user information
 */
export async function fetchComments(recommendationId) {
  try {
    if (!recommendationId) {
      return [];
    }

    const { data, error } = await supabase
      .rpc('get_comments_with_users', {
        recommendation_id_param: recommendationId
      });

    if (error) {
      console.error('Error fetching comments:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching comments:', error);
    return [];
  }
}

/**
 * Post a new comment
 * @param {number} recommendationId - The recommendation ID to comment on
 * @param {string} body - The comment text
 * @param {string} userId - The user ID posting the comment
 * @returns {Object} - Success status and comment data
 */
export async function postComment(recommendationId, body, userId) {
  try {
    if (!recommendationId || !body?.trim() || !userId) {
      return { success: false, error: 'Missing required fields' };
    }

    const { data, error } = await supabase
      .from('comments')
      .insert({
        recommendation_id: recommendationId,
        body: body.trim(),
        user_id: userId
      })
      .select()
      .single();

    if (error) {
      console.error('Error posting comment:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error posting comment:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetch comment counts for multiple recommendations
 * @param {Array} recommendationIds - Array of recommendation IDs
 * @returns {Object} - Object with recommendation_id as key and count as value
 */
export async function fetchCommentCounts(recommendationIds) {
  try {
    if (!recommendationIds || recommendationIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .from('comments')
      .select('recommendation_id')
      .in('recommendation_id', recommendationIds);

    if (error) {
      console.error('Error fetching comment counts:', error);
      return {};
    }

    // Count comments per recommendation
    const commentCounts = {};
    data.forEach(comment => {
      commentCounts[comment.recommendation_id] = (commentCounts[comment.recommendation_id] || 0) + 1;
    });

    return commentCounts;
  } catch (error) {
    console.error('Error fetching comment counts:', error);
    return {};
  }
}
